/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Command execution runner with safety checks
 */
import { execa } from 'execa';
import type { RunPlan } from '../types/index.js';
import { logger } from '../utils/logger.js';

/**
 * Execute a RunPlan
 */
export const executeRunPlan = async (plan: RunPlan, dryRun = false): Promise<void> => {
  const fullCommand = `${plan.cmd} ${plan.args.join(' ')}`;

  if (dryRun) {
    logger.info('Dry run mode - command will not be executed');
    logger.log(`\nWould run: ${fullCommand}`);
    logger.log(`Working directory: ${plan.cwd}`);
    if (plan.env) {
      logger.log(`Environment variables: ${JSON.stringify(plan.env, null, 2)}`);
    }
    return;
  }

  logger.info(`Executing: ${fullCommand}`);
  logger.log('');

  try {
    await execa(plan.cmd, plan.args, {
      cwd: plan.cwd,
      env: { ...process.env, ...plan.env },
      stdio: 'inherit',
      shell: false,
    });
  } catch (error) {
    if (error instanceof Error) {
      logger.error(`Execution failed: ${error.message}`);
    }
    throw error;
  }
};

/**
 * Execute a fix command
 */
export const executeFix = async (command: string, cwd: string): Promise<void> => {
  logger.info(`Running fix: ${command}`);

  try {
    await execa(command, {
      cwd,
      stdio: 'inherit',
      shell: true,
    });
    logger.success('Fix completed successfully');
  } catch (error) {
    if (error instanceof Error) {
      logger.error(`Fix failed: ${error.message}`);
    }
    throw error;
  }
};

